import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

import { type TDashboardFilters } from 'screens/dashboard/Dashboard';
import { DynamicFieldWrapper } from 'components/DynamicFormField/components/DynamicFieldWrapper';
import Label from 'components/Typography/Label';
import type {
  DynamicFieldType,
  FieldOptions,
} from 'components/DynamicFormField/DynamicFormField';
import { defaultDateRangeFilter } from '../../shared/constants';
import { getIsDomainField } from 'components/DynamicFormField/utility/functions';
import {
  getComputedMaxDate,
  isFilterChanged,
  getInitDashboardOrWidgetFiltersFromModuleDynaFields,
} from '../../shared/utils';
import { DASHBOARD_DATE_FILTER_FORMAT } from 'screens/dashboard/constants';

interface QuickDateRangeProps {
  dateRange: [string | null | undefined, string | null | undefined];
  onMonthClick: () => void;
  onWeekClick: () => void;
  onCustomClick: (event: React.MouseEvent<HTMLElement>) => void;
  isMobile?: boolean;
  isLoading?: boolean;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

export const QuickDateRange = ({
  buttonRef,
  dateRange = [null, null],
  onMonthClick,
  onWeekClick,
  onCustomClick,
  isMobile = false,
  isLoading = false,
}: QuickDateRangeProps) => {
  const { t } = useTranslation();

  const [isWeekRange, setIsWeekRange] = useState(false);
  const [isMonthRange, setIsMonthRange] = useState(false);

  useEffect(() => {
    const [startDate = '', endDate = ''] = dateRange ?? [undefined, undefined];
    const startFmt = startDate
      ? dayjs(startDate).format(DASHBOARD_DATE_FILTER_FORMAT)
      : null;
    const endFmt = endDate ? dayjs(endDate).format(DASHBOARD_DATE_FILTER_FORMAT) : null;

    setIsWeekRange(
      startFmt === dayjs().subtract(8, 'day').format(DASHBOARD_DATE_FILTER_FORMAT) &&
        endFmt === dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT),
    );

    setIsMonthRange(
      startFmt === dayjs().subtract(31, 'day').format(DASHBOARD_DATE_FILTER_FORMAT) &&
        endFmt === dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT),
    );
  }, [dateRange]);

  if (!dateRange) {
    return null;
  }

  return (
    <Box
      data-testid="kpi-bar-filters-quick-date-range"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'end',
        gap: 1,
        height: 32,
        '> .MuiButton-text': {
          px: '16px',
        },
      }}
    >
      <Button
        data-testid="kpi-bar-filters-month-button"
        onClick={onMonthClick}
        variant={isMonthRange ? 'contained' : 'text'}
        disableElevation
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
      >
        {t('blox.general.date.range.month')}
      </Button>
      <Button
        data-testid="kpi-bar-filters-week-button"
        onClick={onWeekClick}
        variant={isWeekRange ? 'contained' : 'text'}
        disableElevation
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
      >
        {t('blox.general.date.range.week')}
      </Button>

      {!isMobile && (
        <Button
          data-testid="kpi-bar-filters-custom-button-desktop"
          ref={buttonRef}
          onClick={onCustomClick}
          variant={!(isWeekRange || isMonthRange) ? 'contained' : 'text'}
          disableElevation
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {t('blox.general.date.range.custom')}
        </Button>
      )}
    </Box>
  );
};

export type TKpiBarFiltersProps = {
  dashboardId: string;
  onFiltersChange: (filters: TDashboardFilters) => void;
  initialFilters?: TDashboardFilters;
  enableQuickDateRange?: boolean;
};

export const KpiBarFilters = ({
  dashboardId,
  onFiltersChange,
  initialFilters,
  enableQuickDateRange = false,
}: TKpiBarFiltersProps) => {
  const { t } = useTranslation();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const theme = useTheme();
  const isMdViewport = useMediaQuery(theme.breakpoints.down('md'));

  const [allFiltersAnchorEl, setAllFiltersAnchorEl] = useState<null | HTMLElement>(null);
  const [customDateRangeAnchorEl, setCustomDateRangeAnchorEl] =
    useState<null | HTMLElement>(null);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<TDashboardFilters | undefined>(initialFilters);
  const [hasInitialized, setHasInitialized] = useState(false);

  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const customDateRangeButtonRef = useRef<HTMLButtonElement>(null);
  const initFiltersRef = useRef<TDashboardFilters | null>(null);

  // Fetch dashboard config to get filter definitions
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
  } = useQuery({
    queryKey: ['kpiBarFilters', 'getDashboardById', dashboardId],
    queryFn: async () => {
      return await dataProvider.getDashboardById({
        id: dashboardId,
      });
    },
    enabled: !!dashboardId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const dashboard = dashboardData?.data;

  // Initialize filters when dashboard config is loaded (only once)
  useEffect(() => {
    if (!dashboard || hasInitialized) return;

    // If initialFilters were provided, use them
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setFilters(initialFilters);
      initFiltersRef.current = initialFilters;
      setHasInitialized(true);
      return;
    }

    // Otherwise, initialize from dashboard config
    const initFilters = getInitDashboardOrWidgetFiltersFromModuleDynaFields(
      dashboard?.filters || [],
    );
    initFiltersRef.current = initFilters;
    setFilters(initFilters);
    onFiltersChange(initFilters);
    setHasInitialized(true);
  }, [dashboard, initialFilters, onFiltersChange, hasInitialized]);

  const handleOpenAllFilters = (event: React.MouseEvent<HTMLElement>) => {
    setAllFiltersAnchorEl(event.currentTarget);
  };

  const onSubmit: SubmitHandler<Record<string, any>> = (data) => {
    const newFilters = Object.assign({}, data);

    // Validate date range
    if (newFilters.dateRangeFilter) {
      const [start, end] = newFilters.dateRangeFilter;
      const djsStart = dayjs(start);
      const djsEnd = dayjs(end);

      if (!djsStart.isValid() || !djsEnd.isValid() || djsEnd.isBefore(djsStart)) {
        console.error('End date must be after start date');
        notify(t('blox.dashboards.controls.error'), { type: 'error' });
        return;
      }
    }

    // Update local state
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));

    // Notify parent with updated filters
    onFiltersChange(newFilters);

    notify(t('blox.dashboards.controls.success'), {
      type: 'success',
    });

    setAllFiltersAnchorEl(null);
    setCustomDateRangeAnchorEl(null);
  };

  const onError: SubmitErrorHandler<Record<string, any>> = (errors) => {
    notify(t('blox.actions.save_error'), { type: 'error' });
  };

  const { control, handleSubmit, setValue, reset } = useForm<
    Partial<NonNullable<TDashboardFilters>>
  >({
    defaultValues: filters ?? {},
  });

  // Reset form when filters change
  useEffect(() => {
    const updatedValues = {
      ...filters,
      ...(filters?.startDate &&
        filters?.endDate && {
          dateRangeFilter: [filters.startDate, filters.endDate],
        }),
    };
    reset(updatedValues);
  }, [filters, reset]);

  // Badge count for filter button
  const badgeCount = dashboard?.filters?.reduce(
    (count: number, field: DynamicFieldType) => {
      const formValue = control._formValues[field.name];
      if (isFilterChanged({ formValue: formValue, field: field })) {
        count += 1;
      }
      return count;
    },
    0,
  );

  const handleCustomDateRangeOnClick = (event: React.MouseEvent<HTMLElement>) => {
    if (customDateRangeButtonRef.current) {
      setCustomDateRangeAnchorEl(customDateRangeButtonRef.current);
    }
  };

  const handleWeekDateRangeOnClick = () => {
    const startDate = dayjs().subtract(8, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);
    const endDate = dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);

    setValue('dateRangeFilter', [startDate, endDate]);
    handleSubmit(onSubmit, onError)();
  };

  const dateRangeField = (dashboard?.filters?.find(
    (f: DynamicFieldType) => f.name === 'dateRangeFilter',
  ) || defaultDateRangeFilter) as DynamicFieldType;

  const handleMonthDateRangeOnClick = () => {
    const startDate = dayjs().subtract(31, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);
    const endDate = dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);

    const fieldName = dateRangeField?.name ?? 'dateRangeFilter';

    setValue(fieldName as keyof Partial<NonNullable<TDashboardFilters>>, [
      startDate,
      endDate,
    ]);

    handleSubmit(onSubmit, onError)();
  };

  const handleCloseAllFilters = () => {
    setAllFiltersAnchorEl(null);
    reset(filters ?? initFiltersRef?.current ?? {});
  };

  const handleCloseCustomDateRange = () => {
    setCustomDateRangeAnchorEl(null);
    reset(filters ?? initFiltersRef?.current ?? {});
  };

  if (isDashboardError) {
    return null;
  }

  return (
    <Box
      data-testid="kpi-bar-filters"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        gap: 1,
        position: 'relative',
        opacity: isDashboardLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      {/* Filter button with TuneIcon */}
      <IconButton
        ref={filterButtonRef}
        data-testid="kpi-bar-filters-filter-button"
        onClick={handleOpenAllFilters}
        disabled={isDashboardLoading || !dashboard?.filters?.length}
      >
        <Badge
          badgeContent={badgeCount}
          color="primary"
        >
          {isDashboardLoading ? <CircularProgress size={24} /> : <TuneIcon />}
        </Badge>
      </IconButton>

      {/* Custom date range popover */}
      {dateRangeField && (
        <Popover
          data-testid="kpi-bar-custom-date-range-popover"
          disableEnforceFocus={true}
          open={Boolean(customDateRangeAnchorEl)}
          anchorEl={customDateRangeAnchorEl}
          onClose={handleCloseCustomDateRange}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper
            data-testid="kpi-bar-custom-date-range-form"
            component="form"
            onSubmit={handleSubmit(onSubmit, onError)}
            sx={{ p: 2, maxWidth: 536 }}
          >
            {dateRangeField && (
              <Box
                key={dateRangeField.name}
                sx={{ mb: 2 }}
              >
                <Label>{dateRangeField.label}</Label>
                <Controller
                  name={dateRangeField.name as keyof NonNullable<TDashboardFilters>}
                  control={control}
                  render={({ field: controllerField }) => {
                    const fieldWithMaxDate: DynamicFieldType = {
                      ...dateRangeField,
                      config: {
                        ...(dateRangeField.config ?? {}),
                        maxDate: getComputedMaxDate(dateRangeField.config?.maxDate),
                      },
                    };

                    return (
                      <DynamicFieldWrapper
                        child={fieldWithMaxDate}
                        value={controllerField?.value}
                        onChange={(name, value) => {
                          controllerField.onChange(value);
                        }}
                        isDisabled={false}
                        onValidationChange={(fieldName, isValid) => {
                          setInvalidFields((prev) => {
                            const updated = new Set(prev);
                            isValid ? updated.delete(fieldName) : updated.add(fieldName);
                            return updated;
                          });
                        }}
                      />
                    );
                  }}
                />
              </Box>
            )}
            <Box
              data-testid="kpi-bar-filters-date-range-actions"
              sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
            >
              <Button
                data-testid="kpi-bar-filters-date-range-cancel-button"
                onClick={handleCloseCustomDateRange}
                disableElevation
              >
                {t('blox.actions.cancel')}
              </Button>

              <Button
                variant="contained"
                data-testid="kpi-bar-filters-save-button"
                type="submit"
                disabled={invalidFields.size > 0}
                disableElevation
              >
                {t('blox.actions.save')}
              </Button>
            </Box>
          </Paper>
        </Popover>
      )}

      {/* All filters popover */}
      <Popover
        data-testid="kpi-bar-filters-popover"
        disableEnforceFocus={true}
        open={Boolean(allFiltersAnchorEl)}
        anchorEl={allFiltersAnchorEl}
        onClose={handleCloseAllFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper
          data-testid="kpi-bar-filters-form"
          component="form"
          onSubmit={handleSubmit(onSubmit, onError)}
          sx={{ p: 4, maxWidth: 800 }}
        >
          <Typography
            data-testid="kpi-bar-filters-title"
            variant="heading"
            gutterBottom
          >
            {t('blox.dashboards.kpis.kpi_bar.filters.title')}
          </Typography>

          <Typography
            data-testid="kpi-bar-filters-description"
            component="p"
            variant="caption"
            gutterBottom
          >
            {t('blox.dashboards.kpis.kpi_bar.filters.description')}
          </Typography>

          {/* Quick date actions */}
          {enableQuickDateRange && (
            <QuickDateRange
              buttonRef={customDateRangeButtonRef}
              dateRange={
                (filters?.dateRangeFilter as
                  | [string | null | undefined, string | null | undefined]
                  | undefined) || [null, null]
              }
              onMonthClick={handleMonthDateRangeOnClick}
              onWeekClick={handleWeekDateRangeOnClick}
              onCustomClick={handleCustomDateRangeOnClick}
              isMobile={isMdViewport}
              isLoading={isDashboardLoading}
            />
          )}

          {/* Dynamic filter fields */}
          {!!dashboard?.filters?.length &&
            dashboard?.filters.map((dynamicFieldConfig: DynamicFieldType) => {
              const isPlatformField = dynamicFieldConfig.name === 'platform';
              const isDomainField = getIsDomainField(dynamicFieldConfig);

              // CMSADMIN-2045 - domain/hostname field special ux case
              const fieldOpts: FieldOptions = {
                denumView: isDomainField ? 'domain' : undefined,
              };

              // Platform: Disable "CTV App", "Native App" until ready.
              // Note: TNCMS-556107, ticket was rejected.
              // CTV shows up on (.../editorial/dashboard/video)
              if (isPlatformField) {
                const options = dynamicFieldConfig.config?.options ?? [];
                if (options.length) {
                  dynamicFieldConfig.config = {
                    ...dynamicFieldConfig.config,
                    hideLabel: true,
                    options: options.map((opt: any) => {
                      const value = (opt?.value ?? '').toString().toLowerCase();
                      const isDisabled = value === 'app' || value === 'ctv';
                      return {
                        ...opt,
                        disabled: isDisabled,
                      };
                    }),
                  };
                }
              }

              if (isDomainField) {
                dynamicFieldConfig.config = {
                  ...dynamicFieldConfig.config,
                  hideLabel: true,
                };
              }

              return (
                <Box
                  key={dynamicFieldConfig.name}
                  sx={{ mb: 2 }}
                >
                  <Label>{dynamicFieldConfig.label}</Label>
                  <Controller
                    name={dynamicFieldConfig.name as keyof NonNullable<TDashboardFilters>}
                    control={control}
                    render={({ field: controllerField }) => {
                      const _dynamicFieldConfig = Object.assign({}, dynamicFieldConfig);

                      // Add maxDate to dateRangeFilter
                      if (_dynamicFieldConfig.name === 'dateRangeFilter') {
                        _dynamicFieldConfig.config = {
                          ...(_dynamicFieldConfig.config ?? {}),
                          maxDate: getComputedMaxDate(
                            _dynamicFieldConfig.config?.maxDate,
                          ),
                        };
                      }

                      return (
                        <DynamicFieldWrapper
                          child={_dynamicFieldConfig}
                          value={controllerField?.value}
                          onChange={(name, value) => {
                            controllerField.onChange(value);
                          }}
                          isDisabled={false}
                          fieldOptions={fieldOpts}
                          onValidationChange={(fieldName, isValid) => {
                            setInvalidFields((prev) => {
                              const updated = new Set(prev);
                              isValid
                                ? updated.delete(fieldName)
                                : updated.add(fieldName);
                              return updated;
                            });
                          }}
                        />
                      );
                    }}
                  />
                </Box>
              );
            })}

          <Box
            data-testid="kpi-bar-filters-actions"
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
          >
            <Button
              data-testid="kpi-bar-filters-cancel-button"
              onClick={handleCloseAllFilters}
              disabled={isDashboardLoading}
              disableElevation
            >
              {t('blox.actions.cancel')}
            </Button>

            <Button
              variant="contained"
              data-testid="kpi-bar-filters-save-button"
              type="submit"
              disabled={invalidFields.size > 0 || isDashboardLoading}
              startIcon={isDashboardLoading ? <CircularProgress size={16} /> : undefined}
              disableElevation
            >
              {isDashboardLoading ? t('blox.actions.loading') : t('blox.actions.save')}
            </Button>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default KpiBarFilters;
